import {
  ChecklistItem,
  listChecklistItems,
  updateChecklistItem,
  updateDismissedItem,
} from '@kudos/http-client'
import { useInvalidateQuery } from '@shared/hooks/useInvalidateQuery'
import {
  getGlobalDismissedItems,
  refreshGlobalDismissedItems,
} from '@shared/hooks/useOnboardingGuide'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { ChecklistSection } from '../types/dashboard'
import { checklistSections } from './content/checklistContent'
import { calculateProgress } from './utils/progressUtils'

// Query keys
export const DASHBOARD_DATA_QUERY_KEY = 'dashboard-data' as const

// Aliases
export const SETUP_GUIDE_ALIAS = 'setup-guide'
export const ONBOARDING_TILE_ALIAS = 'onboarding-tile'

export const useDashboardData = () => {
  const invalidateQuery = useInvalidateQuery()

  // Query for checklist items and dismissed items
  const { data: dashboardData, isLoading } = useQuery({
    queryKey: [DASHBOARD_DATA_QUERY_KEY],
    queryFn: async () => {
      // Get all item aliases from the checklist sections
      const allItemAliases = checklistSections.flatMap(section =>
        section.items.map(item => item.key),
      )

      const [apiItems, dismissedAliases] = await Promise.all([
        listChecklistItems(allItemAliases),
        getGlobalDismissedItems(),
      ])

      // Create a map of completed items from API response
      const completedItemsMap = new Map<string, boolean>()
      apiItems.forEach((item: ChecklistItem) => {
        completedItemsMap.set(item.itemAlias, item.completed)
      })

      // Check if setup-guide is dismissed
      const setupGuideDismissed = dismissedAliases.includes(SETUP_GUIDE_ALIAS)

      // Merge API data with default sections
      const updatedSections = checklistSections.map(section => ({
        ...section,
        items: section.items.map(item => ({
          ...item,
          isCompleted: completedItemsMap.get(item.key) ?? false,
        })),
      }))

      return {
        sections: updatedSections,
        dismissedItems: dismissedAliases,
        isDismissed: setupGuideDismissed,
      }
    },
    enabled: true,
  })

  // Default values when data is loading or feature flag is disabled
  const sections = dashboardData?.sections ?? checklistSections
  const dismissedItems = dashboardData?.dismissedItems ?? []
  const isDismissed = dashboardData?.isDismissed ?? false

  // Calculate progress using useMemo for performance
  const progress = useMemo(() => calculateProgress(sections), [sections])

  // Mutation for updating checklist items
  const { mutateAsync: updateChecklistItemMutation, isPending: isUpdateChecklistPending } =
    useMutation({
      mutationFn: updateChecklistItem,
      onSuccess: async () => {
        // Invalidate and refetch dashboard data
        await invalidateQuery([DASHBOARD_DATA_QUERY_KEY])
      },
    })

  // Mutation for updating dismissed items
  const { mutateAsync: updateDismissedItemMutation, isPending: isUpdateDismissedPending } =
    useMutation({
      mutationFn: updateDismissedItem,
      onSuccess: async () => {
        // Refresh global dismissed items and invalidate queries
        refreshGlobalDismissedItems()
        await invalidateQuery([DASHBOARD_DATA_QUERY_KEY])
      },
    })

  const isPending = isUpdateChecklistPending || isUpdateDismissedPending

  // Handle item toggle
  const handleItemToggle = useCallback(
    async (sectionId: string, itemId: string) => {
      // Find current item state
      const currentSection = sections.find((s: ChecklistSection) => s.key === sectionId)
      const currentItem = currentSection?.items.find(
        (i: { key: string; isCompleted: boolean }) => i.key === itemId,
      )
      const newCompletedState = !currentItem?.isCompleted

      // Use mutation to update the backend
      await updateChecklistItemMutation({
        itemAlias: itemId,
        completed: newCompletedState,
      })
    },
    [sections, updateChecklistItemMutation],
  )

  // Dismiss onboarding tile
  const dismissOnboardingTile = useCallback(() => {
    updateDismissedItemMutation({
      itemAlias: ONBOARDING_TILE_ALIAS,
      dismissed: true,
    })
  }, [updateDismissedItemMutation])

  // Mark guide as dismissed
  const dismissGuide = useCallback(() => {
    updateDismissedItemMutation({
      itemAlias: SETUP_GUIDE_ALIAS,
      dismissed: true,
    })
    dismissOnboardingTile()
  }, [dismissOnboardingTile, updateDismissedItemMutation])

  // Relaunch guide
  const relaunchGuide = useCallback(() => {
    updateDismissedItemMutation({
      itemAlias: SETUP_GUIDE_ALIAS,
      dismissed: false,
    })
  }, [updateDismissedItemMutation])

  return {
    sections,
    progress,
    handleItemToggle,
    dismissGuide,
    dismissOnboardingTile,
    relaunchGuide,
    isLoading,
    isDismissed,
    dismissedItems,
    isPending,
  }
}
